import { OverlayToaster, Position, Intent, Toaster2, IToastProps } from '@blueprintjs/core';

// React 18-compatible toaster - use Toaster2 if available, fallback to OverlayToaster
let AppToaster: OverlayToaster | null = null;

// Try to create toaster async (React 18 compatible)
const initToaster = async () => {
  try {
    // Use OverlayToaster.createAsync if available
    if (OverlayToaster.createAsync) {
      AppToaster = await OverlayToaster.createAsync({ 
        position: Position.TOP, 
        maxToasts: 3,
      });
    } else {
      // Fallback to synchronous create (older Blueprint versions)
      AppToaster = OverlayToaster.create({ 
        position: Position.TOP, 
        maxToasts: 3,
      });
    }
  } catch (error) {
    console.error('Failed to initialize toaster:', error);
  }
};

initToaster();

export const ToastService = {
  success(message: string, timeout = 3000) {
    if (!AppToaster) return;
    AppToaster.show({
      message,
      intent: Intent.SUCCESS,
      timeout,
      icon: 'tick-circle',
    });
  },

  error(message: string, timeout = 5000) {
    if (!AppToaster) return;
    AppToaster.show({
      message,
      intent: Intent.DANGER,
      timeout,
      icon: 'error',
    });
  },

  warning(message: string, timeout = 4000) {
    if (!AppToaster) return;
    AppToaster.show({
      message,
      intent: Intent.WARNING,
      timeout,
      icon: 'warning-sign',
    });
  },

  info(message: string, timeout = 3000) {
    if (!AppToaster) return;
    AppToaster.show({
      message,
      intent: Intent.PRIMARY,
      timeout,
      icon: 'info-sign',
    });
  },

  loading(message: string) {
    if (!AppToaster) return;
    return AppToaster.show({
      message,
      intent: Intent.NONE,
      timeout: 0, // Don't auto-dismiss
      icon: 'refresh',
    });
  },

  /**
   * Show a toast with a custom action button
   * @param message - The message to display
   * @param actionText - The text for the action button
   * @param onAction - Callback when the action button is clicked
   * @param intent - The intent/style of the toast
   * @param timeout - Auto-dismiss timeout (0 = no auto-dismiss)
   */
  showWithAction(message: string, actionText: string, onAction: () => void, intent: Intent = Intent.SUCCESS, timeout = 5000) {
    console.log('🔔 [ToastService] showWithAction called:', { message, actionText, hasToaster: !!AppToaster });
    
    if (!AppToaster) {
      console.error('❌ [ToastService] AppToaster not initialized yet!');
      return;
    }
    
    try {
      const toastKey = AppToaster.show({
        message,
        intent,
        timeout,
        icon: intent === Intent.SUCCESS ? 'tick-circle' : 'info-sign',
        action: {
          text: actionText,
          onClick: () => {
            console.log('🎯 [ToastService] Action button clicked!');
            onAction();
          },
        },
      });
      console.log('✅ [ToastService] Toast shown with key:', toastKey);
    } catch (error) {
      console.error('❌ [ToastService] Failed to show toast:', error);
    }
  },

  /**
   * Show a custom toast with full control over props
   */
  show(props: IToastProps) {
    if (!AppToaster) return;
    return AppToaster.show(props);
  },

  dismiss(key: string) {
    if (!AppToaster) return;
    AppToaster.dismiss(key);
  },

  clear() {
    if (!AppToaster) return;
    AppToaster.clear();
  },
};
